import React, { useEffect, useState } from "react";
import { Download, Share, Plus, X, Smartphone, Monitor, Apple } from "lucide-react";
import { useAppText } from "@/contexts/AppTextContext";

const DISMISS_KEY = "bu_install_dashboard_dismissed";

export default function InstallAppCard() {
  const { t } = useAppText();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [openGuide, setOpenGuide] = useState(false);

  // Detect platform once
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isMobile = isIOS || isAndroid;

  useEffect(() => {
    if (window.matchMedia?.("(display-mode: standalone)").matches) { setInstalled(true); return; }
    if (window.navigator.standalone) { setInstalled(true); return; }
    if (localStorage.getItem(DISMISS_KEY) === "1") { setDismissed(true); return; }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (installed || dismissed) return null;

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <>
      <div className="card-surface p-5 sm:p-6 mb-8 relative overflow-hidden" data-testid="install-app-card">
        <button onClick={dismiss} className="absolute top-3 right-3 text-[#8B8794] hover:text-[#1A1422]" data-testid="install-card-dismiss" title="Tutup">
          <X className="w-4 h-4" />
        </button>
        <div className="absolute -right-8 -bottom-8 w-40 h-40 rounded-full bg-[#FF8C00] opacity-10 blur-3xl" />
        <div className="absolute -left-8 -top-8 w-32 h-32 rounded-full bg-[#6B21A8] opacity-10 blur-3xl" />

        <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#FF8C00]/10 flex items-center justify-center flex-shrink-0">
            <Download className="w-7 h-7 text-[#FF8C00]" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-[#1A1422] text-lg mb-1">{t("install.card_title")}</h3>
            <p className="text-sm text-[#5C5868]">
              {isMobile ? t("install.card_subtitle_mobile") : t("install.card_subtitle_desktop")}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            {deferred && (
              <button onClick={handleInstall} className="btn-primary" data-testid="install-card-install-button">
                <Download className="w-4 h-4" /> {t("install.btn_install_now")}
              </button>
            )}
            <button onClick={() => setOpenGuide(true)} className={deferred ? "btn-outline text-sm" : "btn-primary"} data-testid="install-card-guide-button">
              {t("install.btn_how_to")}
            </button>
          </div>
        </div>
      </div>

      {openGuide && <InstallGuideModal onClose={() => setOpenGuide(false)} isIOS={isIOS} isAndroid={isAndroid} t={t} />}
    </>
  );
}

function InstallGuideModal({ onClose, isIOS, isAndroid, t }) {
  const [platform, setPlatform] = useState(isIOS ? "ios" : isAndroid ? "android" : "desktop");

  const tabs = [
    { id: "android", label: "Android", icon: Smartphone },
    { id: "ios", label: "iPhone / iPad", icon: Apple },
    { id: "desktop", label: "Laptop / Desktop", icon: Monitor },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="card-surface max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="install-guide-modal">
        <div className="p-5 border-b border-[#E8E5EE] flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold text-[#1A1422]">{t("install.guide_title")}</h3>
          <button onClick={onClose} className="text-[#5C5868] hover:text-[#1A1422]"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1 px-5 pt-4 border-b border-[#E8E5EE] overflow-x-auto">
          {tabs.map((t) => {
            const I = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setPlatform(t.id)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  platform === t.id ? "border-[#FF8C00] text-[#FF8C00]" : "border-transparent text-[#5C5868]"
                }`}
                data-testid={`guide-tab-${t.id}`}
              >
                <I className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {platform === "android" && (
            <Steps
              intro={t("install.guide_android_intro")}
              steps={[
                <>Buka <strong>BisaUsaha</strong> di browser HP.</>,
                <>Tap tombol <strong>"Install Sekarang"</strong> di atas — atau tap menu titik tiga di pojok kanan atas browser → <strong>"Install app"</strong> / <strong>"Add to Home screen"</strong>.</>,
                <>Konfirmasi dengan tap <strong>"Install"</strong>.</>,
                <>Selesai! 🎉 Icon BisaUsaha akan muncul di home screen HP kamu. Buka langsung dari sana seperti aplikasi biasa.</>,
              ]}
            />
          )}

          {platform === "ios" && (
            <Steps
              intro={t("install.guide_ios_intro")}
              steps={[
                <>Buka <strong>BisaUsaha</strong> di aplikasi <strong>Safari</strong>.</>,
                <>Tap tombol <Share className="inline w-4 h-4 mx-1 text-[#FF8C00]" /> <strong>Share</strong> di bagian bawah layar (kotak dengan panah ke atas).</>,
                <>Scroll ke bawah, cari & tap <strong>"Add to Home Screen"</strong> <Plus className="inline w-4 h-4 mx-1 text-[#FF8C00]" /> (atau "Tambahkan ke Layar Utama").</>,
                <>Atur nama kalau perlu, lalu tap <strong>"Add"</strong> di pojok kanan atas.</>,
                <>Selesai! 🎉 Icon BisaUsaha muncul di home screen iPhone/iPad kamu.</>,
              ]}
            />
          )}

          {platform === "desktop" && (
            <Steps
              intro={t("install.guide_desktop_intro")}
              steps={[
                <>Buka <strong>BisaUsaha</strong> di browser desktop.</>,
                <>Di address bar (sebelah kanan), cari ikon <strong>⊕</strong> atau <strong>komputer kecil</strong>. Klik.</>,
                <>Atau buka menu browser (⋮) → <strong>"Install BisaUsaha..."</strong> / <strong>"Apps"</strong> → <strong>"Install this site as an app"</strong>.</>,
                <>Klik <strong>"Install"</strong>.</>,
                <>Selesai! Aplikasi BisaUsaha muncul di Start Menu (Windows) / Launchpad (Mac). Buka langsung dari sana — tanpa browser bar, full screen.</>,
              ]}
            />
          )}

          <div className="mt-6 pt-5 border-t border-[#E8E5EE] bg-[#FFF8F0] -mx-5 -mb-5 p-5 rounded-b-2xl">
            <p className="text-xs text-[#5C5868] whitespace-pre-line">
              {t("install.guide_benefit")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Steps({ intro, steps }) {
  return (
    <div className="space-y-3">
      {intro && <p className="text-sm text-[#5C5868] mb-3">{intro}</p>}
      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-[#1A1422]">
            <span className="w-7 h-7 rounded-full bg-[#FF8C00] text-white font-bold flex items-center justify-center flex-shrink-0 text-xs">{i + 1}</span>
            <span className="leading-relaxed pt-0.5">{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
